import { axiosInstanceLive } from 'instance/Axios';

/**
 * Utility functions to detect cookie availability and third-party cookie access.
 */

/**
 * Checks if basic cookies are enabled in the browser.
 * @returns {boolean} True if cookies are enabled, false otherwise.
 */
export const checkCookieSupport = () => {
    try {
        if (typeof navigator !== 'undefined' && !navigator.cookieEnabled) {
            return false;
        }
        if (typeof document === 'undefined') {
            return true;
        }

        const testKey = 'acemoney_cookie_test';
        document.cookie = `${testKey}=1; SameSite=None; Secure; path=/`;
        const hasCookie = document.cookie.indexOf(`${testKey}=1`) !== -1;

        // Clean up test cookie
        document.cookie = `${testKey}=1; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure;`;

        return hasCookie;
    } catch (error) {
        console.warn('Error checking cookie support:', error);
        return false;
    }
};

/**
 * Verifies cookie validity with the backend GET API endpoint.
 * @returns {Promise<boolean>} True if backend confirms cookie validity, false otherwise.
 */
export const verifyBackendCookie = async () => {
    try {
        const token = localStorage.getItem('token');
        const latitude = localStorage.getItem('latitude');
        const longitude = localStorage.getItem('longitude');
        const isEmployee = localStorage.getItem('isEmployee');
        let merchantEmpID;
        if (isEmployee === 'true') {
            merchantEmpID = localStorage.getItem('empId');
        }

        const headers = {
            'Content-Type': 'application/json'
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        if (merchantEmpID) {
            headers['Header-Merchant-Employee'] = merchantEmpID;
        }
        if (latitude) {
            headers['X-Latitude'] = latitude;
        }
        if (longitude) {
            headers['X-Longitude'] = longitude;
        }

        const response = await axiosInstanceLive.get('api/V2/moneytransfer/cookieTest', { headers });

        if (response && (response.status === 200 || response.status === 201)) {
            if (response.data && typeof response.data.cookies_enabled === 'boolean') {
                return response.data.cookies_enabled;
            }
            if (response?.data?.status === false || response?.data?.success === false) {
                return false;
            }
            return true;
        }

        return false;
    } catch (error) {
        console.warn('Backend cookie verification API check failed:', error);
        return false;
    }
};

/**
 * Checks if third-party cookies / storage access is permitted.
 * Runs browser cookie checks, Storage Access API check, and backend API check.
 * @returns {Promise<boolean>} Resolves to true if cookies are allowed, false if blocked.
 */
export const checkThirdPartyCookieSupport = async () => {
    // 1. Basic browser cookie check
    const basicSupport = checkCookieSupport();
    if (!basicSupport) {
        return false;
    }

    // 2. Check Storage Access API if supported by browser
    if (typeof document !== 'undefined' && 'hasStorageAccess' in document) {
        try {
            const hasAccess = await document.hasStorageAccess();
            if (!hasAccess) {
                return false;
            }
        } catch (error) {
            console.warn('Storage Access API check failed:', error);
        }
    }

    // 3. Backend API cookie verification check
    const backendValid = await verifyBackendCookie();
    if (!backendValid) {
        return false;
    }

    return true;
};

/**
 * Requests third-party storage access if Storage Access API is available.
 * Must be triggered by a user gesture (e.g. button click).
 * @returns {Promise<boolean>} True if access was granted or already available, false otherwise.
 */
export const requestThirdPartyStorageAccess = async () => {
    if (typeof document !== 'undefined' && 'requestStorageAccess' in document) {
        try {
            await document.requestStorageAccess();
            return true;
        } catch (error) {
            console.warn('Request for storage access denied or failed:', error);
            return false;
        }
    }
    return checkCookieSupport();
};
